import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Smartphone, X, Download, WifiOff, Share2, RefreshCw } from 'lucide-react';

/* ─────────────────────────────────────────────────────────
   Install banner — shows when browser triggers install prompt
───────────────────────────────────────────────────────── */
export const PWAInstallBanner = ({ onInstall, onDismiss }) => {
  const { t, i18n } = useTranslation();
  const [dismissed, setDismissed] = useState(() => {
    const ts = localStorage.getItem('pwa-dismissed');
    return ts && Date.now() - parseInt(ts, 10) < 7 * 24 * 60 * 60 * 1000;
  });

  if (dismissed) return null;
  const dir = i18n.language === 'ar' ? 'rtl' : 'ltr';

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
    localStorage.setItem('pwa-dismissed', Date.now().toString());
  };

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-50 p-4"
      dir={dir}
      style={{ animation: 'slideUp 0.35s cubic-bezier(0.34,1.56,0.64,1) both' }}
    >
      <div className="max-w-lg mx-auto rounded-2xl shadow-2xl border border-white/20 p-4 flex items-center gap-3 sm:gap-4"
        style={{ background: 'linear-gradient(135deg, #0F766E 0%, #4f46e5 100%)' }}
      >
        <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0 backdrop-blur-sm">
          <Smartphone className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-white text-sm">{t('pwa_install_title')}</p>
          <p className="text-xs text-teal-200 mt-0.5">{t('pwa_install_subtitle')}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            id="pwa-install-btn"
            onClick={onInstall}
            className="flex items-center gap-1.5 px-4 py-2 bg-white text-teal-700 text-xs font-bold rounded-xl shadow hover:shadow-lg transition-all hover:scale-105 active:scale-95 min-h-[44px]"
          >
            <Download className="w-3.5 h-3.5" />
            {t('pwa_install_action')}
          </button>
          <button
            onClick={handleDismiss}
            className="w-11 h-11 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            aria-label={t('close')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   Offline indicator bar — appears at top when offline
───────────────────────────────────────────────────────── */
export const OfflineBanner = ({ isOffline }) => {
  const { t, i18n } = useTranslation();
  if (!isOffline) return null;
  const dir = i18n.language === 'ar' ? 'rtl' : 'ltr';

  // Amber, warning-style — actions still queue locally so this is informational,
  // not blocking. Background uses Tailwind amber-500 so dark mode reads consistently.
  return (
    <div
      dir={dir}
      className="fixed top-0 inset-x-0 z-[60] flex items-center justify-center gap-2 py-2 px-4 text-white text-sm font-semibold bg-amber-700 dark:bg-amber-600 shadow-md"
      role="status"
      aria-live="polite"
    >
      <WifiOff className="w-4 h-4 flex-shrink-0" />
      <span>{t('offline_banner')}</span>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   Floating install button — fallback when banner is dismissed
───────────────────────────────────────────────────────── */
export const PWAInstallFAB = ({ onInstall, isInstalled }) => {
  const { t, i18n } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  if (isInstalled || !visible) return null;
  const hasPrompt = !!window.__pwaInstallEvent;
  const dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
  const sideClass = dir === 'rtl' ? 'left-6' : 'right-6';

  const handleClick = () => {
    if (hasPrompt) onInstall();
    else {
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 4000);
    }
  };

  return (
    <div className={`fixed bottom-6 ${sideClass} z-40`} dir={dir}>
      {showTooltip && (
        <div
          className="absolute bottom-full mb-2 px-3 py-2 rounded-xl text-xs text-white whitespace-nowrap"
          style={{ background: '#1e1b4b', boxShadow: '0 4px 16px rgba(0,0,0,0.3)' }}
        >
          {t('pwa_install_browser_hint')}
        </div>
      )}
      <button
        id="pwa-fab-install"
        onClick={handleClick}
        title={t('pwa_install_action')}
        className="flex items-center gap-2 px-4 py-3 rounded-full shadow-2xl text-white text-sm font-bold transition-all hover:scale-110 active:scale-95 min-h-[44px]"
        style={{
          background: hasPrompt
            ? 'linear-gradient(135deg, #0F766E 0%, #4f46e5 100%)'
            : 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
          boxShadow: '0 8px 32px rgba(15,118,110,0.45)',
          animation: 'fabIn 0.5s cubic-bezier(0.34,1.56,0.64,1) both',
        }}
      >
        <Download className="w-4 h-4" />
        <span>{t('pwa_install_action')}</span>
        <style>{`
          @keyframes fabIn {
            from { transform: scale(0) translateY(20px); opacity: 0; }
            to   { transform: scale(1) translateY(0);    opacity: 1; }
          }
        `}</style>
      </button>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   iOS-specific install instructions modal
───────────────────────────────────────────────────────── */
export const IOSInstallHint = () => {
  const { t, i18n } = useTranslation();
  const [show, setShow] = useState(false);
  // [M-29] localStorage access throws outright in sandboxed/private contexts —
  // unguarded here it would crash this component during render.
  const [dismissed, setDismissed] = useState(() => {
    try { return !!localStorage.getItem('ios-hint-dismissed'); } catch { return false; }
  });

  useEffect(() => {
    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    if (isIOS && !isStandalone && !dismissed) {
      // [AUDIT-2026-09 S1.2] Clear the timer on unmount.
      const timer = setTimeout(() => setShow(true), 2500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [dismissed]);

  if (!show) return null;
  const dir = i18n.language === 'ar' ? 'rtl' : 'ltr';

  return (
    <div className="fixed bottom-0 inset-x-0 z-50 p-4" dir={dir}>
      <div
        className="max-w-sm mx-auto rounded-2xl shadow-2xl p-5 text-center"
        style={{
          background: 'linear-gradient(135deg, #0F766E 0%, #4f46e5 100%)',
          color: 'white',
        }}
      >
        <p className="font-bold text-lg mb-2">📱 {t('pwa_ios_title')}</p>
        <p className="text-sm leading-relaxed text-teal-100">{t('pwa_ios_steps')}</p>
        <div className="flex justify-center items-center gap-6 mt-5">
          <div className="flex flex-col items-center gap-1 text-xs text-teal-200">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-2xl">
              <Share2 className="w-5 h-5 text-white" />
            </div>
            {t('pwa_ios_share')}
          </div>
          <div className="text-teal-300 text-2xl">→</div>
          <div className="flex flex-col items-center gap-1 text-xs text-teal-200">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-2xl">➕</div>
            {t('pwa_ios_add')}
          </div>
        </div>
        <button
          onClick={() => {
            setShow(false);
            setDismissed(true);
            localStorage.setItem('ios-hint-dismissed', '1');
          }}
          className="mt-5 text-xs text-teal-300 hover:text-white transition-colors min-h-[44px] px-4"
        >
          {t('skip')}
        </button>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   Update-available banner — appears when a new SW is waiting
───────────────────────────────────────────────────────── */
export const PWAUpdateBanner = ({ updateAvailable, applyUpdate }) => {
  const { t, i18n } = useTranslation();
  const [dismissed, setDismissed] = useState(false);
  if (!updateAvailable || dismissed) return null;
  const dir = i18n.language === 'ar' ? 'rtl' : 'ltr';

  return (
    <div className="fixed top-2 inset-x-0 z-[70] px-4 pointer-events-none" dir={dir}>
      <div className="max-w-md mx-auto rounded-xl shadow-2xl border border-white/20 px-4 py-3 flex items-center gap-3 pointer-events-auto"
        style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #4f46e5 100%)' }}
      >
        <RefreshCw className="w-5 h-5 text-white flex-shrink-0" />
        <div className="flex-1 text-sm font-semibold text-white">
          {t('pwa_update_available')}
        </div>
        <button
          onClick={applyUpdate}
          className="px-3 py-1.5 bg-white text-indigo-700 text-xs font-bold rounded-lg hover:scale-105 transition-transform min-h-[36px]"
        >
          {t('pwa_update_action')}
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white"
          aria-label={t('close')}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
